import { useCallback, useEffect, useRef, useState } from 'react';
import { useManagedTour } from '../hooks/use-managed-tour';
import { useIsInsideManagedTourProvider } from './ManagedTourProvider';
import { TOUR_START_DELAY_MS } from '../constants';
import { useTourStore, selectActiveTourId } from '../tours.store';
import { arePriorOnboardingToursCompleted } from '../onboarding-flow';
import type { ITourConfig } from '../types';

const POLL_INTERVAL_MS = 200;
const MAX_POLL_ATTEMPTS = 50;

/**
 * Checks whether a tour target exists in the DOM.
 * Prepends '#' for plain IDs so querySelector works for both cases.
 */
function targetExists(target: string): boolean {
  const selector = /^[\w-]+$/.test(target) ? `#${target}` : target;
  return document.querySelector(selector) !== null;
}

/**
 * Walks up the DOM tree from `element` and returns the nearest ancestor
 * whose computed `overflow`, `overflowY`, or `overflowX` is `auto` or
 * `scroll`. Returns `null` if no scrollable ancestor exists.
 */
function findScrollableAncestor(element: Element): Element | null {
  let current = element.parentElement;
  while (current) {
    const { overflow, overflowY, overflowX } = window.getComputedStyle(current);
    if (/auto|scroll/.test(`${overflow}${overflowY}${overflowX}`)) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * Props for the TourTrigger component.
 */
interface ITourTriggerProps {
  /** Tour configuration from the registry. */
  tourConfig: ITourConfig;
  /**
   * Alternative CSS selector to watch for instead of the first step's target.
   *
   * When provided, the trigger switches from one-shot polling to a
   * persistent MutationObserver that waits indefinitely for matching
   * elements. It also waits for `activeTourId` to clear before starting,
   * so the tour won't start while another tour is active.
   *
   * Useful for contextual tours whose target elements can appear at any
   * point during the page lifecycle (e.g. connection widgets in a chat).
   */
  watchSelector?: string;
  /**
   * Callback invoked immediately before `startTour()`.
   * Use to perform DOM manipulation such as marking a specific
   * target element (e.g. the last of many matching elements).
   */
  onBeforeStart?: () => void;
  /**
   * Callback invoked on unmount for cleanup (e.g. removing
   * temporary data attributes set by `onBeforeStart`).
   */
  onCleanup?: () => void;
}

/**
 * Starts a tour once when mounted, waiting for the first step's target element
 * to appear in the DOM before triggering. Must be rendered inside a ManagedTourProvider.
 *
 * Respects persistence and active-tour guards from useManagedTour,
 * so the tour only starts if it hasn't been completed and no other tour is active.
 *
 * When `watchSelector` is provided, the trigger uses a MutationObserver
 * instead of polling and reactively waits for other active tours to finish.
 *
 * Logs a warning in development if rendered outside a ManagedTourProvider.
 *
 * @param props - Trigger props
 */
export function TourTrigger(props: ITourTriggerProps) {
  const isInsideProvider = useIsInsideManagedTourProvider();

  if (!isInsideProvider) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[TourTrigger] Must be rendered inside a <ManagedTourProvider>. Tour "${props.tourConfig.id}" will not start.`,
      );
    }
    return null;
  }

  if (props.watchSelector) {
    return <ObserverTriggerInner {...props} />;
  }

  return <PollTriggerInner tourConfig={props.tourConfig} />;
}

/**
 * Default inner trigger: polls for the first step's target element,
 * then calls startTour once it exists.
 */
function PollTriggerInner({ tourConfig }: { tourConfig: ITourConfig }) {
  const { startTour } = useManagedTour(tourConfig);

  // eslint-disable-next-line no-restricted-syntax -- reason: Imperative timers (setTimeout/setInterval) require setup and cleanup on unmount
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let attempts = 0;

    const scheduleStart = () => {
      timeoutId = setTimeout(() => {
        if (!arePriorOnboardingToursCompleted(tourConfig.id)) return;
        startTour();
      }, TOUR_START_DELAY_MS);
    };

    const firstTarget = tourConfig.steps[0]?.target;
    if (!firstTarget || targetExists(firstTarget)) {
      scheduleStart();
    } else {
      intervalId = setInterval(() => {
        attempts += 1;
        if (targetExists(firstTarget)) {
          if (intervalId !== undefined) {
            clearInterval(intervalId);
            intervalId = undefined;
          }
          scheduleStart();
        } else if (attempts >= MAX_POLL_ATTEMPTS) {
          if (intervalId !== undefined) {
            clearInterval(intervalId);
            intervalId = undefined;
          }
          if (process.env.NODE_ENV !== 'production') {
            console.warn(
              `[TourTrigger] Target "${firstTarget}" not found after ${MAX_POLL_ATTEMPTS * POLL_INTERVAL_MS}ms. Tour will not start.`,
            );
          }
        }
      }, POLL_INTERVAL_MS);
    }

    return () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      if (intervalId !== undefined) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; re-running on config change would double-start the tour
  }, []);

  return null;
}

/**
 * Observer-based inner trigger: uses a MutationObserver to watch for
 * `watchSelector` elements, waits for `activeTourId` to clear, runs
 * `onBeforeStart`, then starts the tour after a delay.
 *
 * Once the tour is active, forwards scroll events from the target's
 * nearest scrollable ancestor to `window` so the lattice TourProvider
 * (which only listens for `window.scroll`) can reposition the spotlight.
 */
function ObserverTriggerInner({
  tourConfig,
  watchSelector,
  onBeforeStart,
  onCleanup,
}: ITourTriggerProps) {
  const { startTour } = useManagedTour(tourConfig);
  const activeTourId = useTourStore(selectActiveTourId);
  const startedRef = useRef(false);
  const [tourStarted, setTourStarted] = useState(false);

  // Stable ref so the delayed setTimeout always calls the latest startTour
  // without needing it as an effect dependency (which would cancel the
  // pending timeout on every reference change).
  const startTourRef = useRef(startTour);
  startTourRef.current = startTour;

  const [hasTarget, setHasTarget] = useState(
    () => !!document.querySelector(watchSelector!),
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: MutationObserver requires imperative setup and cleanup
  useEffect(() => {
    const check = () => {
      setHasTarget(!!document.querySelector(watchSelector!));
    };

    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [watchSelector]);

  // eslint-disable-next-line no-restricted-syntax -- reason: Delayed start requires imperative setTimeout with cleanup
  useEffect(() => {
    if (startedRef.current || activeTourId || !hasTarget) return;

    const timeoutId = setTimeout(() => {
      if (!arePriorOnboardingToursCompleted(tourConfig.id)) return;
      onBeforeStart?.();
      startTourRef.current();
      if (useTourStore.getState().activeTourId === tourConfig.id) {
        startedRef.current = true;
        setTourStarted(true);
      }
    }, TOUR_START_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [activeTourId, hasTarget, onBeforeStart, tourConfig.id]);

  // The lattice TourProvider repositions the spotlight on `window.scroll`,
  // but the tour target may live inside an inner scrollable container
  // (e.g. the chat viewport). Forward container scroll events to `window`
  // so the spotlight tracks the target as chat content streams in.
  const forwardScrollEvents = useCallback((selector: string) => {
    const target = document.querySelector(selector);
    if (!target) return;

    const scrollParent = findScrollableAncestor(target);
    if (!scrollParent) return;

    let rafId: number | null = null;
    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        // Dispatch from scrollParent so capture-phase listeners that read
        // `event.target.contains(...)` (e.g. Radix Tooltip) see a real
        // Element target; `bubbles: true` still reaches window listeners.
        scrollParent.dispatchEvent(new Event('scroll', { bubbles: true }));
        rafId = null;
      });
    };

    scrollParent.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollParent.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  // eslint-disable-next-line no-restricted-syntax -- reason: Imperative scroll listener bridging inner container to window.scroll for lattice spotlight repositioning
  useEffect(() => {
    if (!tourStarted || !watchSelector) return;
    return forwardScrollEvents(watchSelector);
  }, [tourStarted, watchSelector, forwardScrollEvents]);

  // eslint-disable-next-line no-restricted-syntax -- reason: Cleanup on unmount
  useEffect(() => {
    return () => onCleanup?.();
  }, [onCleanup]);

  return null;
}
